import { type ReactElement, type ReactNode } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useFormControl } from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

import Icon from 'shared/atoms/Icon';
import T from 'shared/atoms/Typography';
import Tooltip from 'shared/atoms/Tooltip';
import type { BackgroundThemes } from 'shared/const';
import theme from 'shared/themes/default';

const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  justify-content: start;
  margin-bottom: ${theme.spacing.xs};
`;

interface FormLabelProps {
  id: string;
  infoText?: string | ReactNode;
  label: string | ReactNode;
  length?: number;
  maxLength?: number;
  theme?: BackgroundThemes;
}

const FormLabel = ({
  id,
  infoText,
  label,
  length,
  maxLength,
  theme: themeLayout,
}: FormLabelProps) => {
  const { t } = useTranslation();
  const { required } = useFormControl() || {};

  const sx = {
    color:
      themeLayout === 'light'
        ? theme.palette.mainDark
        : theme.palette.lightPure,
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: '140%',
    position: 'relative',
    transform: 'none',
    '.MuiInputLabel-asterisk': {
      display: 'none',
    },
    '&.Mui-disabled': {
      color:
        themeLayout === 'light'
          ? theme.palette.mainDark
          : theme.palette.lightPure,
    },
    '&.Mui-focused': {
      color:
        themeLayout === 'light'
          ? theme.palette.mainDark
          : theme.palette.lightPure,
    },
    '&.Mui-error': {
      color:
        themeLayout === 'light'
          ? theme.palette.mainDark
          : theme.palette.lightPure,
    },
  };

  const getCharCounterColor = (count: number) => {
    if (count <= 0) {
      return themeLayout === 'light'
        ? theme.palette.errorDark
        : theme.palette.errorLight;
    }
    return themeLayout === 'light'
      ? theme.palette.disabledDark
      : theme.palette.disabledLight;
  };

  return (
    <LabelWrapper>
      {label && (
        <InputLabel htmlFor={id} sx={sx} shrink>
          {label}
          {!required && (
            <T
              color={
                themeLayout === 'light'
                  ? theme.palette.mainDark
                  : theme.palette.lightPure
              }
              component="span"
              fontWeight="medium"
              italic
            >
              {` (${t('generic.optional')})`}
            </T>
          )}
        </InputLabel>
      )}
      {infoText && (
        <Tooltip
          title={
            typeof infoText === 'string' ? infoText : (infoText as ReactElement)
          }
          maxWidth="200px"
          arrow
        >
          <Icon
            icon={InfoOutlinedIcon}
            color={
              themeLayout === 'light'
                ? theme.palette.mainDark
                : theme.palette.lightPure
            }
            size="19px"
            sx={{
              cursor: 'pointer',
            }}
          />
        </Tooltip>
      )}
      {maxLength && (
        <T
          color={getCharCounterColor(maxLength - (length || 0))}
          fontWeight="medium"
          sx={{ marginLeft: 'auto' }}
          variant="helperText"
        >
          {t('generic.charactersLeft', { count: maxLength - (length || 0) })}
        </T>
      )}
    </LabelWrapper>
  );
};

export default FormLabel;
export type { FormLabelProps };
